import React, { useState, useEffect, useCallback } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { API_URL as API } from '@/utils/apiConfig';
import {
  Coins, TrendingUp, DollarSign, Activity, Zap, Users, Bell, BellOff,
  Mail, Smartphone, Plus, Trash2, Loader2, RefreshCw, AlertTriangle, Check
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, CartesianGrid, Legend
} from 'recharts';


const FEATURE_LABELS = { chat: 'Chat', title_gen: 'Title Gen', share_summary: 'Share Summary' };
const PIE_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];

function formatTokens(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n?.toString() || '0';
}
function formatCost(n) {
  if (n === undefined || n === null) return '$0.00';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function StatCard({ icon: Icon, label, value, sub, color = '#6366f1' }) {
  const { isDark, colors } = useAdminTheme();
  return (
    <div className="rounded-2xl p-5" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
      data-testid={`token-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon className="h-4.5 w-4.5" style={{ color }} />
        </div>
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: colors.textMuted }}>{label}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color: colors.text }}>{value}</p>
      {sub && <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{sub}</p>}
    </div>
  );
}

function ChartCard({ title, children, className = '' }) {
  const { isDark, colors } = useAdminTheme();
  return (
    <div className={`rounded-2xl p-5 ${className}`} style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}>
      <h3 className="text-sm font-semibold mb-4" style={{ color: colors.text }}>{title}</h3>
      {children}
    </div>
  );
}

export default function AdminTokenUsagePage() {
  const { isDark, colors } = useAdminTheme();
  const [overview, setOverview] = useState(null);
  const [daily, setDaily] = useState([]);
  const [projections, setProjections] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(false);

  // Alert form
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [thresholds, setThresholds] = useState([]);
  const [newThreshold, setNewThreshold] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, dailyRes, projRes, alertRes] = await Promise.all([
        axios.get(`${API}/admin/token-usage/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/token-usage/daily?days=30`, { withCredentials: true }),
        axios.get(`${API}/admin/token-usage/projections`, { withCredentials: true }),
        axios.get(`${API}/admin/token-usage/alerts`, { withCredentials: true }),
      ]);
      setOverview(ovRes.data);
      setDaily(dailyRes.data);
      setProjections(projRes.data);
      setAlerts(alertRes.data);
      const cfg = alertRes.data?.config || {};
      setAlertEnabled(cfg.enabled !== false);
      setEmailEnabled(cfg.email_enabled !== false);
      setPushEnabled(cfg.push_enabled !== false);
      setThresholds(cfg.thresholds || []);
    } catch (err) {
      toast.error('Failed to load token usage data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveAlerts = async () => {
    setAlertsLoading(true);
    try {
      await axios.post(`${API}/admin/token-usage/alerts`, {
        enabled: alertEnabled, thresholds, email_enabled: emailEnabled, push_enabled: pushEnabled,
      }, { withCredentials: true });
      toast.success('Alert settings saved');
    } catch { toast.error('Failed to save alerts'); }
    finally { setAlertsLoading(false); }
  };

  const addThreshold = () => {
    const amt = parseFloat(newThreshold);
    if (!amt || amt <= 0) return;
    if (thresholds.some(t => t.amount_usd === amt)) { toast.error('Threshold already exists'); return; }
    setThresholds(prev => [...prev, { amount_usd: amt, label: `$${amt}` }].sort((a, b) => a.amount_usd - b.amount_usd));
    setNewThreshold('');
  };

  const removeThreshold = (amt) => setThresholds(prev => prev.filter(t => t.amount_usd !== amt));

  const checkAlertsNow = async () => {
    try {
      const res = await axios.post(`${API}/admin/token-usage/check-alerts`, {}, { withCredentials: true });
      const d = res.data;
      if (d.alerts_sent?.length) toast.success(`Alerts triggered for: ${d.alerts_sent.map(a => '$' + a).join(', ')}`);
      else toast.info(`No alerts triggered. Monthly cost: ${formatCost(d.current_monthly_cost)}`);
    } catch { toast.error('Alert check failed'); }
  };

  const tooltipStyle = { background: isDark ? '#1e293b' : '#fff', border: `1px solid ${colors.cardBorder}`, borderRadius: 12, fontSize: 12 };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.accent }} />
        </div>
      </AdminLayout>
    );
  }

  const t = overview?.total || {};
  const m = overview?.this_month || {};
  const td = overview?.today || {};
  const avg = overview?.daily_avg || {};
  const proj = projections || {};

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-7xl" data-testid="token-usage-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: colors.text }}>AI Token Usage</h1>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Monitor costs, usage trends, and set budget alerts</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm" className="gap-2" data-testid="refresh-token-data"
            style={{ color: colors.text, borderColor: colors.cardBorder, background: isDark ? 'transparent' : '#fff' }}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Coins} label="Total Tokens" value={formatTokens(t.total_tokens)} sub={`${t.total_requests || 0} requests`} color="#6366f1" />
          <StatCard icon={DollarSign} label="Total Cost" value={formatCost(t.total_cost)} sub="All time" color="#ef4444" />
          <StatCard icon={Activity} label="This Month" value={formatCost(m.cost)} sub={`${formatTokens(m.tokens)} tokens`} color="#f59e0b" />
          <StatCard icon={Zap} label="Today" value={formatCost(td.cost)} sub={`${td.requests || 0} requests`} color="#10b981" />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Daily Cost Trend */}
          <ChartCard title="Daily Cost Trend (30d)" className="lg:col-span-2">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily}>
                  <defs>
                    <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: colors.textMuted }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: colors.textMuted }} tickFormatter={v => `$${v}`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${v.toFixed(4)}`, 'Cost']} labelFormatter={l => l} />
                  <Area type="monotone" dataKey="cost" stroke="#6366f1" fill="url(#costGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          {/* By Feature Pie */}
          <ChartCard title="Usage by Feature">
            <div className="h-64 flex items-center justify-center">
              {overview?.by_feature?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={overview.by_feature.map(f => ({ ...f, name: FEATURE_LABELS[f.feature] || f.feature }))}
                      dataKey="tokens" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={2}>
                      {overview.by_feature.map((f, i) => <Cell key={f.feature} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatTokens(v), 'Tokens']} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm" style={{ color: colors.textMuted }}>No data yet</p>
              )}
            </div>
          </ChartCard>
        </div>

        {/* Daily Tokens Bar Chart */}
        <ChartCard title="Daily Token Volume (Input vs Output)">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: colors.textMuted }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: colors.textMuted }} tickFormatter={formatTokens} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [formatTokens(v), name === 'input_tokens' ? 'Input' : 'Output']} />
                <Bar dataKey="input_tokens" stackId="a" fill="#6366f1" radius={[0, 0, 0, 0]} name="Input" />
                <Bar dataKey="output_tokens" stackId="a" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Output" />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* Projections + Alerts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Projections */}
          <ChartCard title="Cost Projections">
            <div className="space-y-4" data-testid="cost-projections">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: '30 Day', value: proj.projections?.['30_day'], color: '#10b981' },
                  { label: '60 Day', value: proj.projections?.['60_day'], color: '#f59e0b' },
                  { label: '90 Day', value: proj.projections?.['90_day'], color: '#ef4444' },
                ].map(p => (
                  <div key={p.label} className="rounded-xl p-4 text-center" style={{ background: `${p.color}10`, border: `1px solid ${p.color}30` }}>
                    <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>{p.label}</p>
                    <p className="text-lg font-bold" style={{ color: p.color }}>{formatCost(p.value)}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2 text-xs" style={{ color: colors.textSecondary }}>
                <div className="flex justify-between"><span>Avg daily cost</span><span className="font-medium" style={{ color: colors.text }}>{formatCost(proj.avg_daily_cost)}</span></div>
                <div className="flex justify-between"><span>Avg cost/user/day</span><span className="font-medium" style={{ color: colors.text }}>{formatCost(proj.avg_cost_per_user_per_day)}</span></div>
                <div className="flex justify-between"><span>User growth rate</span><span className="font-medium" style={{ color: proj.user_growth_rate > 0 ? '#10b981' : colors.text }}>{proj.user_growth_rate || 0}%</span></div>
                <div className="flex justify-between"><span>Active users (7d)</span><span className="font-medium" style={{ color: colors.text }}>{proj.current_active_users || 0}</span></div>
              </div>
            </div>
          </ChartCard>

          {/* Budget Alerts */}
          <ChartCard title="Budget Alerts">
            <div className="space-y-4" data-testid="budget-alerts">
              {/* Enable toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {alertEnabled ? <Bell className="h-4 w-4 text-indigo-400" /> : <BellOff className="h-4 w-4 text-slate-400" />}
                  <span className="text-sm font-medium" style={{ color: colors.text }}>Alerts {alertEnabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                <button onClick={() => setAlertEnabled(!alertEnabled)} className="relative w-10 h-5 rounded-full transition-colors"
                  style={{ background: alertEnabled ? '#6366f1' : isDark ? '#475569' : '#cbd5e1' }} data-testid="alert-toggle">
                  <div className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform" style={{ left: alertEnabled ? '22px' : '2px' }} />
                </button>
              </div>

              {/* Notification channels */}
              <div className="flex gap-3">
                <button onClick={() => setEmailEnabled(!emailEnabled)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  style={{ background: emailEnabled ? 'rgba(99,102,241,0.15)' : isDark ? 'rgba(51,65,85,0.5)' : '#f1f5f9', color: emailEnabled ? '#6366f1' : colors.textMuted }}
                  data-testid="email-alert-toggle">
                  <Mail className="h-3 w-3" /> Email
                </button>
                <button onClick={() => setPushEnabled(!pushEnabled)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  style={{ background: pushEnabled ? 'rgba(99,102,241,0.15)' : isDark ? 'rgba(51,65,85,0.5)' : '#f1f5f9', color: pushEnabled ? '#6366f1' : colors.textMuted }}
                  data-testid="push-alert-toggle">
                  <Smartphone className="h-3 w-3" /> Push
                </button>
              </div>

              {/* Thresholds */}
              <div className="space-y-2">
                <p className="text-xs font-medium" style={{ color: colors.textSecondary }}>Monthly spend thresholds</p>
                <div className="flex flex-wrap gap-2">
                  {thresholds.map(t => (
                    <div key={t.amount_usd} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                      style={{ background: isDark ? 'rgba(51,65,85,0.5)' : '#f1f5f9', color: colors.text }}>
                      ${t.amount_usd}
                      <button onClick={() => removeThreshold(t.amount_usd)} className="hover:text-red-400 ml-1" data-testid={`remove-threshold-${t.amount_usd}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: colors.textMuted }} />
                    <input type="number" value={newThreshold} onChange={e => setNewThreshold(e.target.value)} placeholder="Amount"
                      className="w-full rounded-xl pl-8 pr-3 py-2 text-sm outline-none" style={{ background: isDark ? colors.input : '#f8fafc', border: `1px solid ${colors.inputBorder}`, color: colors.text }}
                      onKeyDown={e => e.key === 'Enter' && addThreshold()} data-testid="new-threshold-input" />
                  </div>
                  <Button onClick={addThreshold} size="sm" className="rounded-xl gap-1" data-testid="add-threshold-btn">
                    <Plus className="h-3.5 w-3.5" /> Add
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button onClick={saveAlerts} disabled={alertsLoading} size="sm" className="flex-1 rounded-xl gap-1.5" data-testid="save-alerts-btn">
                  {alertsLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save Settings
                </Button>
                <Button onClick={checkAlertsNow} variant="outline" size="sm" className="rounded-xl gap-1.5" data-testid="check-alerts-btn"
                  style={{ color: colors.text, borderColor: colors.cardBorder, background: isDark ? 'transparent' : '#fff' }}>
                  <AlertTriangle className="h-3.5 w-3.5" /> Check Now
                </Button>
              </div>

              {/* Alert History */}
              {alerts?.history?.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs font-medium mb-2" style={{ color: colors.textSecondary }}>Recent Alerts</p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {alerts.history.map((h, i) => (
                      <div key={h.triggered_at + i} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg"
                        style={{ background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)' }}>
                        <span style={{ color: colors.text }}>${h.threshold_usd} threshold crossed</span>
                        <span style={{ color: colors.textMuted }}>{new Date(h.triggered_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ChartCard>
        </div>

        {/* Top Users */}
        {overview?.top_users?.length > 0 && (
          <ChartCard title="Top Token Consumers">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="top-users-table">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                    {['User', 'Tokens', 'Cost', 'Requests'].map(h => (
                      <th key={h} className="text-left py-2 px-3 text-xs font-medium uppercase tracking-wide" style={{ color: colors.textMuted }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {overview.top_users.map((u, i) => (
                    <tr key={u.user_id} style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                      <td className="py-2.5 px-3">
                        <div>
                          <p className="text-sm font-medium" style={{ color: colors.text }}>{u.name || 'Unknown'}</p>
                          <p className="text-xs" style={{ color: colors.textMuted }}>{u.email}</p>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 font-mono text-xs" style={{ color: colors.text }}>{formatTokens(u.tokens)}</td>
                      <td className="py-2.5 px-3 font-mono text-xs" style={{ color: '#ef4444' }}>{formatCost(u.cost)}</td>
                      <td className="py-2.5 px-3 font-mono text-xs" style={{ color: colors.textMuted }}>{u.requests}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        )}

        {/* Model Pricing Reference */}
        <ChartCard title="Model Pricing Reference">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { model: 'Gemini 2.5 Flash', input: '$0.15', output: '$0.60' },
              { model: 'Gemini 2.0 Flash', input: '$0.10', output: '$0.40' },
              { model: 'GPT-4o', input: '$2.50', output: '$10.00' },
              { model: 'GPT-4o Mini', input: '$0.15', output: '$0.60' },
            ].map(p => (
              <div key={p.model} className="rounded-xl p-3 text-xs" style={{ background: isDark ? 'rgba(15,23,42,0.5)' : '#f8fafc', border: `1px solid ${colors.cardBorder}` }}>
                <p className="font-medium mb-1.5" style={{ color: colors.text }}>{p.model}</p>
                <p style={{ color: colors.textMuted }}>In: {p.input}/1M &middot; Out: {p.output}/1M</p>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </AdminLayout>
  );
}
